import React, { useEffect, useMemo, useState } from "react";
import NavBar from "@/components/NavBar";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Search, Edit3, UserPlus, Ban, RotateCcw, Save, X, Mail, ShieldCheck, Upload } from "lucide-react";
import BulkImportContactsModal from "@/components/directory/BulkImportContactsModal";

/**
 * iter71e · Club Vendor Directory.
 *
 * Full CRUD-lite management surface for the private per-club vendor
 * contact list first introduced in iter71 P0. Staff can:
 *   - Search / filter by name, company, email, brand, category
 *   - Toggle "Include Inactive"
 *   - Edit a contact inline (email/name/title/phone/brand/category/notes)
 *   - Deactivate / reactivate a contact
 *   - Invite a contact to become an Approved Vendor (opens vendor apply
 *     form pre-filled via a query string)
 *
 * The directory unifies TWO sources so a club staff member sees a single
 * pane of glass over every vendor relationship they might reach out to:
 *   • Approved Vendors  (platform-wide, `vendors.status == "approved"`)
 *   • Club-Added Contacts (private per-club reps in `club_vendor_contacts`)
 * Approved vendor rows are read-only and hide the edit / deactivate
 * actions. A colored badge distinguishes the two sources at a glance,
 * and a filter chip lets staff isolate either group.
 */
export default function ClubVendorDirectoryPage() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ approved_vendors: 0, club_contacts: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");
  const [q, setQ] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [sourceFilter, setSourceFilter] = useState("all"); // "all" | "approved_vendor" | "club_contact"
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});
  const [showImport, setShowImport] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/club/vendor-contacts", {
        params: {
          q: q.trim() || undefined,
          include_inactive: includeInactive ? true : undefined,
          source: sourceFilter === "all" ? undefined : sourceFilter,
        },
      });
      setRows(data.contacts || []);
      setCounts(data.counts || { approved_vendors: 0, club_contacts: 0, total: (data.contacts || []).length });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeInactive, sourceFilter]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const shownRows = useMemo(() => rows, [rows]);
  const flashOk = (m) => { setFlash(m); setTimeout(() => setFlash(""), 2000); };

  const startEdit = (row) => {
    setEditingId(row.contact_id);
    setDraft({
      vendor_company_name: row.vendor_company_name || "",
      representative_name: row.representative_name || "",
      representative_title: row.representative_title || "",
      representative_phone: row.representative_phone || "",
      brand: row.brand || "",
      category: row.category || "",
      notes: row.notes || "",
    });
  };
  const cancelEdit = () => { setEditingId(null); setDraft({}); };

  const saveEdit = async (contact_id) => {
    setError("");
    try {
      const { data } = await api.patch(`/club/vendor-contacts/${contact_id}`, draft);
      setRows((rs) => rs.map((r) => r.contact_id === contact_id ? data.contact : r));
      flashOk("Contact updated.");
      cancelEdit();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const toggleActive = async (row) => {
    const target = row.active === false ? true : false;
    const verb = target ? "Reactivate" : "Deactivate";
    if (!window.confirm(`${verb} ${row.representative_name} (${row.vendor_company_name})?`)) return;
    try {
      const { data } = await api.patch(`/club/vendor-contacts/${row.contact_id}`, { active: target });
      setRows((rs) => rs.map((r) => r.contact_id === row.contact_id ? data.contact : r));
      flashOk(target ? "Reactivated." : "Deactivated.");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const inviteToApprove = (row) => {
    // Pre-fill the public vendor apply page with what we already know.
    const q = new URLSearchParams({
      company: row.vendor_company_name || "",
      name: row.representative_name || "",
      email: row.representative_email || "",
      phone: row.representative_phone || "",
      invited_by_contact_id: row.contact_id,
    });
    window.open(`/vendors/apply?${q.toString()}`, "_blank", "noopener");
  };

  return (
    <>
      <NavBar />
      <main className="max-w-7xl mx-auto px-5 sm:px-10 py-10" data-testid="club-vendor-directory-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="overline">Directory</div>
            <h1 className="font-serif text-4xl text-[#1A422D] mt-2">Vendor Contacts</h1>
            <p className="mt-2 text-sm text-[#4A4A4A] max-w-2xl">
              Your club&rsquo;s private list of vendor representatives. Contacts here are visible only to your staff and can receive Vendor Packets without an approved-vendor account.
            </p>
          </div>
          <a href="/orders" className="btn-secondary text-xs" data-testid="cvd-back-orders">
            Back to Orders
          </a>
          <button
            type="button"
            onClick={() => setShowImport(true)}
            data-testid="cvd-open-import"
            className="inline-flex items-center gap-2 border border-[#1A422D] text-[#1A422D] hover:bg-[#1A422D] hover:text-[#F9F8F4] px-4 py-2 text-[11px] uppercase tracking-[0.22em]"
          >
            <Upload size={13} /> Import CSV
          </button>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[280px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7A7A7A]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, company, email, brand, category…"
              className="input-field w-full pl-9"
              data-testid="cvd-search"
            />
          </div>
          <div className="inline-flex items-center gap-1 border border-[#E5E3D8] bg-white p-0.5" role="tablist" aria-label="Filter by source">
            {[
              { key: "all", label: `All (${counts.total})`, testid: "cvd-source-all" },
              { key: "approved_vendor", label: `Approved (${counts.approved_vendors})`, testid: "cvd-source-approved" },
              { key: "club_contact", label: `Club-Added (${counts.club_contacts})`, testid: "cvd-source-club" },
            ].map((opt) => (
              <button
                key={opt.key}
                type="button"
                data-testid={opt.testid}
                onClick={() => setSourceFilter(opt.key)}
                className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] transition ${
                  sourceFilter === opt.key
                    ? "bg-[#1A422D] text-[#F9F8F4]"
                    : "text-[#4A4A4A] hover:bg-[#F3F1E9]"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-[#4A4A4A]">
            <input type="checkbox" checked={includeInactive}
                   onChange={(e) => setIncludeInactive(e.target.checked)}
                   data-testid="cvd-include-inactive"
                   className="h-4 w-4 accent-[#1A422D]" />
            Include inactive
          </label>
        </div>

        {error && (
          <div className="mt-4 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] px-3 py-2 text-sm"
               data-testid="cvd-error">{error}</div>
        )}
        {flash && (
          <div className="mt-4 border border-[#1A422D] bg-[#EFF3EC] text-[#1A422D] px-3 py-2 text-sm"
               data-testid="cvd-flash">{flash}</div>
        )}

        <div className="mt-6 border border-[#E5E3D8] bg-white overflow-x-auto" data-testid="cvd-table">
          {loading ? (
            <div className="p-10 text-sm text-[#7A7A7A]">Loading contacts…</div>
          ) : shownRows.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#7A7A7A]" data-testid="cvd-empty">
              {sourceFilter === "approved_vendor"
                ? "No approved vendors on the platform yet."
                : sourceFilter === "club_contact"
                  ? "No club-added contacts yet — add one from any order’s Send Vendor Packet dialog."
                  : "No vendors or contacts to show — add a contact from any order’s Send Vendor Packet dialog."}
            </div>
          ) : (
            <table className="w-full text-left">
              <thead className="bg-[#F3F1E9]">
                <tr className="text-xs uppercase tracking-wider text-[#4A4A4A]">
                  <th className="px-4 py-3">Vendor / Rep</th>
                  <th className="px-4 py-3">Email · Phone</th>
                  <th className="px-4 py-3">Brand · Category</th>
                  <th className="px-4 py-3">Packets · Intro</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {shownRows.map((r) => {
                  const isApproved = r.contact_type === "approved_vendor" || r.source === "approved_vendor";
                  const readOnly = r.read_only === true || isApproved;
                  const editing = editingId === r.contact_id && !readOnly;
                  const inactive = r.active === false;
                  return (
                    <tr key={r.contact_id}
                        className={`border-t border-[#E5E3D8] align-top ${inactive ? "opacity-60" : ""}`}
                        data-testid={`cvd-row-${r.contact_id}`}>
                      <td className="px-4 py-3">
                        {editing ? (
                          <div className="space-y-1">
                            <input value={draft.vendor_company_name}
                                   onChange={(e) => setDraft({ ...draft, vendor_company_name: e.target.value })}
                                   className="input-field w-full text-sm" placeholder="Company" />
                            <input value={draft.representative_name}
                                   onChange={(e) => setDraft({ ...draft, representative_name: e.target.value })}
                                   className="input-field w-full text-sm" placeholder="Rep name" />
                            <input value={draft.representative_title}
                                   onChange={(e) => setDraft({ ...draft, representative_title: e.target.value })}
                                   className="input-field w-full text-sm" placeholder="Title (optional)" />
                          </div>
                        ) : (
                          <>
                            <div className="font-serif text-base text-[#1A422D]">{r.vendor_company_name}</div>
                            <div className="text-sm text-[#1A1A1A]">{r.representative_name}
                              {r.representative_title && <span className="text-[#7A7A7A]"> · {r.representative_title}</span>}
                            </div>
                            <div className="mt-1 flex items-center gap-2 flex-wrap">
                              {isApproved ? (
                                <span
                                  data-testid={`cvd-badge-approved-${r.contact_id}`}
                                  className="text-[10px] px-1.5 py-0.5 border border-[#1A422D] bg-[#EFF3EC] text-[#1A422D] uppercase tracking-[0.16em] inline-flex items-center gap-1"
                                >
                                  <ShieldCheck size={10} /> Approved Vendor
                                </span>
                              ) : (
                                <span
                                  data-testid={`cvd-badge-club-${r.contact_id}`}
                                  className="text-[10px] px-1.5 py-0.5 border border-[#B59A5A] bg-[#FBF6E8] text-[#8a6f33] uppercase tracking-[0.16em]"
                                >
                                  Club-Added Contact
                                </span>
                              )}
                              {inactive && (
                                <span className="text-[10px] px-1.5 py-0.5 border border-[#7A7A7A] text-[#4A4A4A] uppercase tracking-[0.16em]">Inactive</span>
                              )}
                            </div>
                          </>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="text-[#1A1A1A]">{r.representative_email}</div>
                        {editing ? (
                          <input value={draft.representative_phone}
                                 onChange={(e) => setDraft({ ...draft, representative_phone: e.target.value })}
                                 className="input-field w-full text-sm mt-1" placeholder="Phone (optional)" />
                        ) : (
                          r.representative_phone && <div className="text-[#7A7A7A]">{r.representative_phone}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {editing ? (
                          <div className="space-y-1">
                            <input value={draft.brand}
                                   onChange={(e) => setDraft({ ...draft, brand: e.target.value })}
                                   className="input-field w-full text-sm" placeholder="Brand" />
                            <input value={draft.category}
                                   onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                                   className="input-field w-full text-sm" placeholder="Category" />
                          </div>
                        ) : (
                          <>
                            {r.brand && <div className="text-[#1A1A1A]">{r.brand}</div>}
                            {r.category && <div className="text-[#7A7A7A]">{r.category}</div>}
                            {!r.brand && !r.category && <span className="text-[#7A7A7A]">—</span>}
                          </>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {isApproved ? (
                          <span className="text-[#7A7A7A]">—</span>
                        ) : (
                          <>
                            <div className="text-[#1A1A1A] font-mono">{r.total_packets_sent || 0}</div>
                            {r.introduction_email_sent ? (
                              <div className="text-[10px] uppercase tracking-[0.16em] text-[#1A422D] mt-0.5">Intro sent</div>
                            ) : (
                              <div className="text-[10px] uppercase tracking-[0.16em] text-[#8a6f33] mt-0.5">Intro pending</div>
                            )}
                          </>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {readOnly ? (
                          <span
                            data-testid={`cvd-readonly-${r.contact_id}`}
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-[#7A7A7A]"
                            title="Approved vendors are managed by the platform. Reach out through your normal order flow."
                          >
                            <ShieldCheck size={10} /> Platform Managed
                          </span>
                        ) : editing ? (
                          <div className="inline-flex items-center gap-2">
                            <button onClick={() => saveEdit(r.contact_id)}
                                    data-testid={`cvd-save-${r.contact_id}`}
                                    className="inline-flex items-center gap-1 bg-[#1A422D] text-[#F9F8F4] px-3 py-1.5 text-[11px] uppercase tracking-[0.18em]">
                              <Save size={12} /> Save
                            </button>
                            <button onClick={cancelEdit}
                                    data-testid={`cvd-cancel-${r.contact_id}`}
                                    className="inline-flex items-center gap-1 border border-[#7A7A7A] text-[#4A4A4A] px-3 py-1.5 text-[11px] uppercase tracking-[0.18em]">
                              <X size={12} /> Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="inline-flex items-center gap-2 flex-wrap justify-end">
                            <button onClick={() => startEdit(r)}
                                    data-testid={`cvd-edit-${r.contact_id}`}
                                    title="Edit"
                                    className="inline-flex items-center gap-1 border border-[#1A422D] text-[#1A422D] px-2.5 py-1.5 text-[11px] uppercase tracking-[0.18em] hover:bg-[#1A422D] hover:text-[#F9F8F4]">
                              <Edit3 size={12} /> Edit
                            </button>
                            <button onClick={() => inviteToApprove(r)}
                                    data-testid={`cvd-invite-${r.contact_id}`}
                                    title="Send them the Approved Vendor apply form"
                                    className="inline-flex items-center gap-1 border border-[#B59A5A] text-[#8a6f33] px-2.5 py-1.5 text-[11px] uppercase tracking-[0.18em] hover:bg-[#B59A5A] hover:text-[#F9F8F4]">
                              <UserPlus size={12} /> Invite
                            </button>
                            <button onClick={() => toggleActive(r)}
                                    data-testid={`cvd-toggle-${r.contact_id}`}
                                    title={inactive ? "Reactivate" : "Deactivate"}
                                    className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] uppercase tracking-[0.18em] border ${inactive ? "border-[#1A422D] text-[#1A422D]" : "border-[#A33] text-[#8a1a1a]"}`}>
                              {inactive ? <><RotateCcw size={12} /> Reactivate</> : <><Ban size={12} /> Deactivate</>}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="mt-6 flex items-center justify-between flex-wrap gap-3 text-[11px] uppercase tracking-[0.22em] text-[#7A7A7A]">
          <span data-testid="cvd-total-summary">
            Showing {shownRows.length} · {counts.approved_vendors} Approved · {counts.club_contacts} Club-Added
          </span>
          <span className="flex items-center gap-2">
            <Mail size={12} /> Add new contacts from any order&rsquo;s <em className="font-serif not-italic text-[#1A422D]">Send Vendor Packet</em> dialog.
          </span>
        </div>
      </main>
      <BulkImportContactsModal
        open={showImport}
        onClose={() => setShowImport(false)}
        onImported={() => load()}
      />
    </>
  );
}
